import { lazy, Suspense, useMemo, useState, useEffect, useCallback } from "react";
import { HomepageProvider} from './pages/homepage/homepageContext'
import { AuthProvider, useAuthContext } from "./context/authContext";

import { Routes, Route, Navigate } from "react-router-dom";
import PrintDailySlips from "./pages/print_daily_slips/PrintDailySlips.page";
import PrintOneSlip from "./pages/print_daily_slips/PrintOneSlip";

import { ThemeProvider, alpha, createTheme } from "@mui/material/styles";
import { CircularProgress, CssBaseline } from "@mui/material";
import Navbar from "./components/topbar/Navbar";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase/firestore.utils";
import { DEFAULT_THEME, resolveUserTheme } from "./theme/Theme";

const Timecard = lazy(() => import("./pages/accounting/time_card/Timecard.page"));
const Accounting = lazy(() => import("./pages/accounting/Accounting.page"));
const HomePage = lazy(() => import("./pages/homepage/HomePage.page"));
const Invoice = lazy(() => import("./pages/invoice/Invoice.page"))
const EquipmentCatalog = lazy(() =>
  import("./pages/products/equipment_catalog/EquipmentCatalog.page")
);
const MaintenancePage = lazy(() =>
  import("./pages/maintenance/MaintenancePage")
);
const PartsCatalog = lazy(() =>
  import("./pages/products/parts_catalog/PartsCatalog.page")
);
const PartsQuote = lazy(() => import("./pages/parts_quote/PartsQuote.page"));
const Schedule = lazy(() => import("./pages/schedule/Schedule.page"));
const ServicesCatalog = lazy(() =>
  import("./pages/products/services/ServicesCatalog.page")
);
const Projects = lazy(() => import("./pages/projects/Projects.page"));
const Settings = lazy(() => import("./pages/settings/Settings.page"));
const SignIn = lazy(() => import("./pages/sign-in/SignIn"));
const PrivacyPolicy = lazy(() => import("./pages/privacy/PrivacyPolicy.page"));

const ProtectedRoute = ({ children }) => {
  const { isActiveUser, isLoading } = useAuthContext();

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!isActiveUser) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { authUser, isActiveUser, isLoading } = useAuthContext();

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      {isActiveUser ? <Navbar authUser={authUser} /> : null}
      <Suspense fallback={<CircularProgress />}>
        <Routes>
          <Route
            path="/"
            element={isActiveUser ? <HomePage /> : <SignIn />}
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute>
                <Schedule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment_catalog"
            element={
              <ProtectedRoute>
                <EquipmentCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/maintenance"
            element={
              <ProtectedRoute>
                <MaintenancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parts_catalog"
            element={
              <ProtectedRoute>
                <PartsCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parts_quote"
            element={
              <ProtectedRoute>
                <PartsQuote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services_catalog"
            element={
              <ProtectedRoute>
                <ServicesCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting"
            element={
              <ProtectedRoute>
                <Accounting />
              </ProtectedRoute>
            }
          />
          <Route  
            path="/timecard"
            element = {
              <ProtectedRoute>
                <Timecard />
              </ProtectedRoute>
            }
          /> 
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/print_daily_slips/:state"
            element={
              <ProtectedRoute>
                <PrintDailySlips />
              </ProtectedRoute>
            }
          />
          <Route
            path="/print_one_slip/:state"
            element={
              <ProtectedRoute>
                <PrintOneSlip />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoice"
            element={
              <ProtectedRoute>
                <Invoice />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <main style={{ padding: "1rem" }}>
                <p>Theres nothing here!</p>
              </main>
            }
          />
        </Routes>
      </Suspense>
    </>
  );
};

function App() {
  const { authUser, isActiveUser } = useAuthContext();

  const [themeColor, setThemeColor] = useState(DEFAULT_THEME);

  useEffect(() => {
    if (!isActiveUser || !authUser?.uid) {
      setThemeColor(DEFAULT_THEME);
      return undefined;
    }

    const subscribeToUserTheme = onSnapshot(
      doc(db, "users", authUser.uid),
      (docSnap) => {
        if (docSnap.exists()) {
          setThemeColor(resolveUserTheme(docSnap.data()?.theme));
        }
      },
      (error) => {
        console.error("Firestore listener error (user theme):", error);
      },
    );

    return () => {
      subscribeToUserTheme();
    };
  }, [authUser?.uid, isActiveUser]);

  const getDesignTokens = useCallback(
    (mode) => ({
      typography: {
        brand: {
          fontFamily: "Rajdhani, sans-serif",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        },
      },
      palette: {
        mode,
        ...(mode === "light"
          ? {
              primary: {
                main: themeColor.color,
              },
              secondary: {
                main: "#f50057", //red
              },
            }
          : {
              background: {
                default: "#333", //black
                paper: "#333", //black
              },
              primary: {
                main: themeColor.color, //dark teal
              },
              secondary: {
                main: "#f50057", //red
              },
            }),
      },
      components: {
        MuiButton: {
          styleOverrides: {
            outlined: ({ theme, ownerState }) => {
              const paletteColor =
                ownerState.color &&
                ownerState.color !== "inherit" &&
                theme.palette[ownerState.color]?.main
                  ? theme.palette[ownerState.color]
                  : theme.palette.primary;

              if (mode === "dark") {
                const fill = alpha(paletteColor.main, 0.18);
                const hoverFill = alpha(paletteColor.main, 0.32);
                const text = paletteColor.light || "#fff";
                return {
                  // MUI 7 outlined buttons use CSS variables; && beats variant hover.
                  "&&:not(.Mui-disabled)": {
                    "--variant-outlinedBg": fill,
                    "--variant-outlinedBorder": paletteColor.main,
                    "--variant-outlinedColor": text,
                    backgroundColor: fill,
                    borderColor: paletteColor.main,
                    color: text,
                    "@media (hover: hover)": {
                      "&:hover": {
                        "--variant-outlinedBg": hoverFill,
                        "--variant-outlinedBorder": paletteColor.main,
                        backgroundColor: hoverFill,
                        borderColor: paletteColor.main,
                      },
                    },
                  },
                };
              }

              return {
                "&&:not(.Mui-disabled)": {
                  "--variant-outlinedBorder": paletteColor.main,
                  borderColor: paletteColor.main,
                },
              };
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: ({ theme, ownerState }) => {
              const paletteColor =
                ownerState.color &&
                ownerState.color !== "inherit" &&
                ownerState.color !== "standard" &&
                theme.palette[ownerState.color]?.main
                  ? theme.palette[ownerState.color]
                  : theme.palette.primary;

              if (mode === "dark") {
                const text = paletteColor.light || "#fff";
                return {
                  "&&:not(.Mui-disabled)": {
                    borderColor: paletteColor.main,
                    color: text,
                    "&:hover": {
                      backgroundColor: alpha(paletteColor.main, 0.24),
                      borderColor: paletteColor.main,
                    },
                    "&.Mui-selected": {
                      backgroundColor: alpha(paletteColor.main, 0.32),
                      borderColor: paletteColor.main,
                      color: text,
                      "&:hover": {
                        backgroundColor: alpha(paletteColor.main, 0.4),
                      },
                    },
                  },
                };
              }

              return {
                "&&:not(.Mui-disabled)": {
                  borderColor: paletteColor.main,
                },
              };
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            outlined: ({ theme, ownerState }) => {
              const paletteColor =
                ownerState.color &&
                ownerState.color !== "default" &&
                theme.palette[ownerState.color]?.main
                  ? theme.palette[ownerState.color]
                  : theme.palette.primary;

              if (mode === "dark") {
                return {
                  "&&:not(.Mui-disabled)": {
                    backgroundColor: alpha(paletteColor.main, 0.18),
                    borderColor: paletteColor.main,
                    color: paletteColor.light || "#fff",
                  },
                };
              }

              return {
                "&&:not(.Mui-disabled)": {
                  borderColor: paletteColor.main,
                },
              };
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              "& .MuiInputBase-input": {
                color: mode === "dark" ? "#fff" : "#000",
              },
              "& .MuiInputLabel-root": {
                color: mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
              },
            },
          },
        },
      },
    }),
    [themeColor.color]
  );

  const currentTheme = useMemo(
    () => createTheme(getDesignTokens(themeColor.mode)),
    [themeColor.mode, getDesignTokens]
  );

  return (
    <ThemeProvider theme={currentTheme}>
      <HomepageProvider>
        <CssBaseline />
        <AppRoutes />
      </HomepageProvider>
    </ThemeProvider>
  );
}

function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithAuth;
